import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAdminApi } from '../hooks/useAdminApi';
import { useWebSocketLocation } from '../../hooks/useWebSocketLocation';
import { 
  MapPin, 
  RefreshCw,
  Maximize,
  Minimize,
  Users,
  Navigation,
  Wifi,
  WifiOff,
  Clock,
  Truck,
  Play,
  Square,
  Radio,
  ArrowLeft
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const API_URL = process.env.REACT_APP_BACKEND_URL;

// SVG paths for different marker types
const MARKER_PATHS = {
  // Circle for stopped vehicles
  circle: 'M 0,-8 A 8,8 0 1,1 0,8 A 8,8 0 1,1 0,-8 Z',
  
  // Square for slow movement
  square: 'M -7,-7 L 7,-7 L 7,7 L -7,7 Z',
  
  // Arrow/chevron for moving vehicles (pointing up, will be rotated)
  arrow: 'M 0,-12 L 8,8 L 0,4 L -8,8 Z'
};

// Get marker icon based on speed and heading - with label offset
// --- MARKER-COLOR-STATUS-V1 ---
const getDriverMarkerIcon = (speed, heading, status) => {
  // status: 'online' (<2 min) | 'recent' (2-15 min) | 'offline' (>15 min)
  const baseColor =
    status === 'online' ? '#22c55e' :
    status === 'recent' ? '#f59e0b' :
    '#64748b'; // offline / unknown -> gray

  // Determine shape based on speed
  let path, scale, rotation;
  
  if (speed < 2) {
    // Stopped - Circle
    path = MARKER_PATHS.circle;
    scale = 1;
    rotation = 0;
  } else if (speed < 10) {
    // Slow movement - Square
    path = MARKER_PATHS.square;
    scale = 1;
    rotation = heading || 0; // Slight rotation based on heading
  } else {
    // Moving - Arrow pointing in direction
    path = MARKER_PATHS.arrow;
    scale = 1.2;
    rotation = heading || 0;
  }
  
  return {
    path: path,
    scale: scale,
    fillColor: baseColor,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    rotation: rotation,
    anchor: { x: 0, y: 0 },
    labelOrigin: { x: 0, y: -20 } // Position label above marker
  };
};

const MapPage = () => {
  const { get } = useAdminApi();
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [useWebSocket, setUseWebSocket] = useState(true);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const simulationRef = useRef(null);

  // WebSocket connection for real-time updates
  const { 
    isConnected: wsConnected, 
    drivers: wsDrivers, 
    lastUpdate: wsLastUpdate,
    refresh: wsRefresh,
    error: wsError 
  } = useWebSocketLocation('admin');

  // Fallback HTTP polling state
  const [httpDrivers, setHttpDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use WebSocket drivers if connected, otherwise fall back to HTTP
  const drivers = useWebSocket && wsConnected ? wsDrivers : httpDrivers;

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get('/saas/location/drivers');
      if (result.success) {
        setHttpDrivers(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
    setLoading(false);
  }, [get]);

  // Fallback to HTTP polling if WebSocket is disabled or not connected
  useEffect(() => {
    if (!useWebSocket || !wsConnected) {
      fetchLocations();
      const interval = setInterval(fetchLocations, 60000);
      return () => clearInterval(interval);
    }
  }, [useWebSocket, wsConnected, fetchLocations]);

  // Initialize Google Maps
  useEffect(() => {
    let isMounted = true;
    
    const initMap = () => {
      if (!isMounted) return;
      if (window.google && window.google.maps && mapContainerRef.current && !mapRef.current) {
        try {
          // Default center: Montreal
          mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
            center: { lat: 45.5017, lng: -73.5673 },
            zoom: 10,
            styles: [
              { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
              { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a2e" }] },
              { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
              { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
              { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
              { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
              { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
            ],
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
          });
          setMapLoaded(true);
        } catch (error) {
          console.error('Error initializing map:', error);
        }
      }
    };

    // Load Google Maps script only if not already loaded
    if (!window.google || !window.google.maps) {
      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
        script.async = true;
        script.onload = () => {
          if (isMounted) initMap();
        };
        document.head.appendChild(script);
      } else {
        // Script exists, wait for it to load
        existingScript.addEventListener('load', initMap);
        // Also try immediately in case it's already loaded
        setTimeout(initMap, 100);
      }
    } else {
      initMap();
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Update markers on map
  const updateMarkers = useCallback((driversData) => {
    if (!mapRef.current || !window.google || !window.google.maps) return;

    try {
      // Clear old markers safely
      Object.values(markersRef.current).forEach(marker => {
        try {
          if (marker && typeof marker.setMap === 'function') {
            marker.setMap(null);
          }
        } catch (e) {
          // Ignore errors from already removed markers
        }
      });
      markersRef.current = {};

      if (!driversData || driversData.length === 0) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasValidPositions = false;

      driversData.forEach(driver => {
        if (!driver.latitude || !driver.longitude) return;
        
        hasValidPositions = true;
        const position = { lat: Number(driver.latitude), lng: Number(driver.longitude) };
        bounds.extend(position);

        // Statut envoyé par le backend (basé sur fraîcheur 2 min / 15 min).
        // Fallback sur timestamp legacy si status non fourni.
        const status = driver.status || (() => {
          if (!driver.timestamp) return 'offline';
          const ageMin = (Date.now() - new Date(driver.timestamp).getTime()) / 60000;
          if (ageMin < 2) return 'online';
          if (ageMin < 15) return 'recent';
          return 'offline';
        })();
        const isOnline = status === 'online';
        const isRecent = status === 'recent';

        // Determine icon based on speed and heading
        const speed = driver.speed != null ? Number(driver.speed) : 0;
        const heading = driver.heading != null ? Number(driver.heading) : 0;
        
        // Get marker icon based on movement state + freshness status
        const markerIcon = getDriverMarkerIcon(speed, heading, status);

        // Create custom marker with driver name label
        const marker = new window.google.maps.Marker({
          position,
          map: mapRef.current,
          title: driver.driver_name || 'Chauffeur',
          icon: markerIcon,
          label: {
            text: driver.driver_name || 'Chauffeur',
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 'bold',
            className: 'driver-marker-label'
          }
        });

        // Info window with movement status
        const movementStatus = speed < 2 ? 'À l\'arrêt' : speed < 10 ? 'Déplacement lent' : 'En mouvement';
        const statusBadge = isOnline
          ? '🟢 En ligne'
          : isRecent
            ? '🟠 Récent (2-15 min)'
            : '⚫ Hors ligne';
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; min-width: 150px;">
              <strong style="color: #000;">${driver.driver_name || 'Chauffeur'}</strong>
              <p style="margin: 4px 0; color: #666; font-size: 12px;">
                ${statusBadge}
              </p>
              <p style="margin: 0; color: #666; font-size: 12px;">
                ${movementStatus} ${speed > 0 ? `• ${speed.toFixed(0)} km/h` : ''}
              </p>
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(mapRef.current, marker);
          setSelectedDriver(driver);
        });

        markersRef.current[driver.driver_id] = marker;
      });

      // Fit bounds if we have positions
      if (hasValidPositions && driversData.length > 0) {
        try {
          mapRef.current.fitBounds(bounds);
          if (driversData.length === 1) {
            mapRef.current.setZoom(14);
          }
        } catch (e) {
          console.error('Error fitting bounds:', e);
        }
      }
    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchLocations, 60000);
    return () => clearInterval(interval);
  }, [fetchLocations]);

  useEffect(() => {
    if (mapLoaded && drivers.length > 0) {
      updateMarkers(drivers);
    }
  }, [mapLoaded, drivers, updateMarkers]);

  const toggleFullscreen = () => {
    const container = document.querySelector('.admin-map-container');
    if (!document.fullscreenElement) {
      container?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const centerOnDriver = (driver) => {
    if (mapRef.current && driver.latitude && driver.longitude) {
      mapRef.current.panTo({ lat: driver.latitude, lng: driver.longitude });
      mapRef.current.setZoom(15);
      setSelectedDriver(driver);
    }
  };

  const onlineDrivers = drivers.filter(d => {
    if (!d.timestamp) return false;
    return new Date(d.timestamp) > new Date(Date.now() - 5 * 60 * 1000);
  });

  // Simulation GPS - Simule un chauffeur qui se déplace à Montréal
  const startSimulation = async () => {
    setSimulating(true);
    
    // Route simulée autour de Montréal avec coordonnées et vitesses variables
    const route = [
      { lat: 45.5048, lng: -73.5538, speed: 0 },     // Vieux-Port - départ (arrêté)
      { lat: 45.5068, lng: -73.5568, speed: 8 },     // Démarrage lent
      { lat: 45.5088, lng: -73.5618, speed: 35 },    // Accélération
      { lat: 45.5128, lng: -73.5658, speed: 45 },    // Rue Sherbrooke
      { lat: 45.5168, lng: -73.5708, speed: 55 },    // Avenue du Parc
      { lat: 45.5208, lng: -73.5778, speed: 50 },    // Vers Mile End
      { lat: 45.5248, lng: -73.5828, speed: 40 },    // Ralentissement
      { lat: 45.5268, lng: -73.5858, speed: 5 },     // Presque arrêté (feu rouge)
      { lat: 45.5288, lng: -73.5888, speed: 35 },    // Redémarrage
      { lat: 45.5308, lng: -73.5918, speed: 45 },    // Plateau Mont-Royal
      { lat: 45.5348, lng: -73.5868, speed: 55 },    // Boulevard Saint-Laurent
      { lat: 45.5388, lng: -73.5808, speed: 60 },    // Vitesse max
      { lat: 45.5428, lng: -73.5738, speed: 50 },    // Vers Rosemont
      { lat: 45.5468, lng: -73.5668, speed: 40 },    // Ralentissement
      { lat: 45.5508, lng: -73.5598, speed: 0 },     // Arrêt final
    ];
    
    let index = 0;
    
    // Calculate heading between two points
    const calculateHeading = (lat1, lng1, lat2, lng2) => {
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const lat1Rad = lat1 * Math.PI / 180;
      const lat2Rad = lat2 * Math.PI / 180;
      const x = Math.sin(dLng) * Math.cos(lat2Rad);
      const y = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng);
      let heading = Math.atan2(x, y) * 180 / Math.PI;
      return (heading + 360) % 360;
    };
    
    const sendLocation = async () => {
      if (index >= route.length) {
        index = 0; // Boucle la route
      }
      
      const point = route[index];
      const nextPoint = route[(index + 1) % route.length];
      const heading = calculateHeading(point.lat, point.lng, nextPoint.lat, nextPoint.lng);
      
      const token = localStorage.getItem('admin_token');
      
      try {
        await fetch(`${API_URL}/api/saas/location/simulate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            latitude: point.lat,
            longitude: point.lng,
            speed: point.speed,
            heading: heading,
            accuracy: 10
          })
        });
        
        // Actualiser la carte
        fetchLocations();
      } catch (error) {
        console.error('Erreur simulation:', error);
      }
      
      index++;
    };
    
    // Envoyer immédiatement puis toutes les 3 secondes (pour voir les changements)
    await sendLocation();
    simulationRef.current = setInterval(sendLocation, 3000);
  };

  const stopSimulation = () => {
    if (simulationRef.current) {
      clearInterval(simulationRef.current);
      simulationRef.current = null;
    }
    setSimulating(false);
  };

  // Cleanup simulation on unmount
  useEffect(() => {
    return () => {
      if (simulationRef.current) {
        clearInterval(simulationRef.current);
      }
    };
  }, []);

  return (
    <div className="admin-map-container h-full flex flex-col bg-slate-950">
      {/* Header */}
      <div className="p-4 border-b border-slate-800 flex-shrink-0">
        {/* Back Button */}
        <Link 
          to="/admin/dashboard"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors mb-3"
        >
          <ArrowLeft size={18} />
          <span>Retour au tableau de bord</span>
        </Link>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <MapPin className="text-amber-500" />
              Carte en direct
            </h1>
            <p className="text-slate-400 flex items-center gap-2">
              {onlineDrivers.length} chauffeur(s) en ligne
              {useWebSocket && wsConnected ? (
                <span className="flex items-center gap-1 text-green-400 text-xs">
                  <Radio size={12} className="animate-pulse" />
                  WebSocket actif
                </span>
              ) : (
                <span className="text-slate-500 text-xs">· Polling HTTP: 1 min</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* WebSocket Toggle */}
            <button
              onClick={() => setUseWebSocket(!useWebSocket)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                useWebSocket 
                  ? 'bg-green-600/20 text-green-400 border border-green-600/50' 
                  : 'bg-slate-800 text-slate-400'
              }`}
              title={useWebSocket ? 'WebSocket activé' : 'WebSocket désactivé'}
            >
              <Radio size={16} />
              {useWebSocket ? 'Temps réel' : 'Polling'}
            </button>
            {/* Bouton Simulation */}
            <button
              onClick={simulating ? stopSimulation : startSimulation}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                simulating 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
              data-testid="simulation-btn"
            >
              {simulating ? (
                <>
                  <Square size={18} />
                  Arrêter
                </>
              ) : (
                <>
                  <Play size={18} />
                  Simuler GPS
                </>
              )}
            </button>
            <button
              onClick={() => {
                if (useWebSocket && wsConnected) {
                  wsRefresh();
                } else {
                  fetchLocations();
                }
              }}
              disabled={loading}
              className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Actualiser
            </button>
            <button
              onClick={toggleFullscreen}
              className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Map Area */}
      <div className="flex-1 flex min-h-0">
        {/* Map */}
        <div className="flex-1 relative">
          <div 
            ref={mapContainerRef} 
            className="absolute inset-0"
            style={{ background: '#1a1a2e' }}
          />
          
          {/* Fallback if Google Maps not loaded */}
          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
              <div className="text-center">
                <MapPin className="text-slate-600 mx-auto mb-4" size={64} />
                <p className="text-slate-400 text-lg mb-2">Chargement de la carte...</p>
                <p className="text-slate-500 text-sm">
                  Configuration Google Maps requise
                </p>
              </div>
            </div>
          )}

          {/* No drivers message */}
          {mapLoaded && drivers.length === 0 && !loading && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-slate-900/90 rounded-xl p-6 text-center">
                <Users className="text-slate-600 mx-auto mb-3" size={40} />
                <p className="text-slate-400">Aucun chauffeur avec position active</p>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="absolute bottom-4 left-4 bg-slate-900/90 backdrop-blur rounded-lg p-3 text-sm">
            <p className="text-slate-400 text-xs mb-2 font-medium">Légende</p>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-slate-300">Arrêté</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500" style={{ transform: 'rotate(45deg)' }} />
                  <span className="text-slate-300">Lent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-b-[10px] border-l-transparent border-r-transparent border-b-green-500" />
                  <span className="text-slate-300">En mouvement</span>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span>En ligne (&lt; 2 min)</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1"></span>Récent (2-15 min)</span>
                <span><span className="inline-block w-2 h-2 rounded-full bg-slate-500 mr-1"></span>Hors ligne</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Driver List */}
        <div className="w-80 bg-slate-900 border-l border-slate-800 hidden lg:flex flex-col">
          <div className="p-4 border-b border-slate-800 flex-shrink-0">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Truck size={18} />
              Chauffeurs ({drivers.length})
            </h3>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            {drivers.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">
                Aucun chauffeur localisé
              </p>
            ) : (
              drivers.map(driver => {
                // Statut envoyé par le backend (basé sur fraîcheur 2 min / 15 min)
                // Fallback sur timestamp legacy si pas de status
                const status = driver.status || (
                  driver.timestamp && new Date(driver.timestamp) > new Date(Date.now() - 2 * 60 * 1000)
                    ? 'online'
                    : driver.timestamp && new Date(driver.timestamp) > new Date(Date.now() - 15 * 60 * 1000)
                    ? 'recent'
                    : 'offline'
                );
                const isOnline = status === 'online';
                const isRecent = status === 'recent';
                const statusLabel = isOnline ? 'En ligne' : isRecent ? 'Récent' : 'Hors ligne';
                const statusColor = isOnline ? 'green' : isRecent ? 'amber' : 'slate';
                
                return (
                  <button
                    key={driver.driver_id}
                    onClick={() => centerOnDriver(driver)}
                    data-testid={`driver-card-${driver.driver_id}`}
                    className={`w-full p-3 rounded-lg text-left transition-colors mb-1 ${
                      selectedDriver?.driver_id === driver.driver_id
                        ? 'bg-amber-500/20 border border-amber-500/50'
                        : 'hover:bg-slate-800 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-${statusColor}-500/20`}>
                        {isOnline || isRecent ? (
                          <Wifi className={`text-${statusColor}-500`} size={18} />
                        ) : (
                          <WifiOff className="text-slate-500" size={18} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">
                          {driver.driver_name || 'Chauffeur'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          <span className={`text-${statusColor}-400 font-medium`}>{statusLabel}</span>
                          {driver.speed != null && (
                            <span>· {Number(driver.speed).toFixed(0)} km/h</span>
                          )}
                        </div>
                      </div>
                      <div className={`w-2 h-2 rounded-full bg-${statusColor}-500`} />
                    </div>
                    
                    {(driver.timestamp || driver.minutes_since_update != null) && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                        <Clock size={12} />
                        <span>
                          {driver.minutes_since_update != null && driver.minutes_since_update < 60
                            ? `Il y a ${Math.round(driver.minutes_since_update)} min`
                            : driver.timestamp
                              ? new Date(driver.timestamp).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
                              : '—'}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
